import { useState } from 'react';
import { ApiError, passwordApi } from './api';
import { PasswordInput } from './ui';

/**
 * "Forgot password", for both portals.
 *
 * The API answers 202 whether or not the address exists, deliberately — anything else
 * is an oracle that turns a list of email addresses into a list of our clients. So
 * this screen says "if that address is on the system, a link is on its way" and never
 * "no such account", because it genuinely has not been told which.
 */
export function ForgotPasswordForm({ portal, onBack }: {
  portal: 'client' | 'platform';
  onBack: () => void;
}) {
  const [isBusy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Card title="Forgot your password?" subtitle="We will email you a link to set a new one.">
      {sent ? (
        <>
          <p className="mt-5 rounded border border-(--color-line) px-3 py-3 text-sm">
            If that address is on the system, a link is on its way. It works once and expires in an
            hour.
          </p>
          <BackButton onClick={onBack} />
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const email = String(new FormData(event.currentTarget).get('email') ?? '');
            setBusy(true);
            setError(null);
            void passwordApi.forgotPassword(email, portal)
              .then(() => setSent(true))
              .catch((cause: unknown) => setError(
                cause instanceof ApiError && cause.code === 'email_unavailable'
                  ? 'Password reset is not switched on yet. Ask your administrator to set a new password for you.'
                  : 'Could not send that. Try again in a moment.'))
              .finally(() => setBusy(false));
          }}
        >
          {error && (
            <p className="mt-4 rounded border border-(--color-bad) px-3 py-2 text-xs text-(--color-bad)">{error}</p>
          )}

          <label className="mt-5 block text-xs font-medium tracking-wide text-(--color-muted) uppercase">
            Email
            <input
              name="email" type="email" required autoComplete="username" autoFocus
              className="mt-1.5 w-full rounded border border-(--color-line) bg-transparent px-3 py-2 text-sm outline-none focus:border-(--color-brand)"
            />
          </label>

          <button
            type="submit" disabled={isBusy}
            className="mt-6 w-full rounded bg-(--color-brand-strong) px-3 py-2 text-sm font-medium text-(--color-brand-ink) disabled:opacity-50"
          >
            {isBusy ? 'Sending…' : 'Send the link'}
          </button>

          <BackButton onClick={onBack} />
        </form>
      )}
    </Card>
  );
}

/**
 * Where the emailed link lands.
 *
 * Used, expired and never-existed are one message, because the API answers them
 * identically — telling them apart would tell an attacker whether a token was ever
 * real. The offer to start again is on the same screen, since that is the only thing
 * to do about any of the three.
 */
export function ResetPasswordForm({ onDone, onRestart }: {
  onDone: () => void;
  onRestart: () => void;
}) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [isBusy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <Card title="That link is incomplete" subtitle="Ask for a new one.">
        <button type="button" onClick={onRestart}
                className="mt-6 w-full rounded bg-(--color-brand-strong) px-3 py-2 text-sm font-medium text-(--color-brand-ink)">
          Start again
        </button>
      </Card>
    );
  }

  if (done) {
    return (
      <Card title="Password changed" subtitle="Every other session has been signed out.">
        <button type="button" onClick={onDone}
                className="mt-6 w-full rounded bg-(--color-brand-strong) px-3 py-2 text-sm font-medium text-(--color-brand-ink)">
          Sign in
        </button>
      </Card>
    );
  }

  return (
    <Card title="Choose a new password" subtitle="At least twelve characters.">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const password = String(form.get('password') ?? '');

          if (password !== String(form.get('confirm') ?? '')) {
            setError('Those two do not match.');
            return;
          }

          setBusy(true);
          setError(null);
          void passwordApi.resetPassword(token, password)
            .then(() => setDone(true))
            .catch((cause: unknown) => setError(
              cause instanceof ApiError && cause.code === 'token_invalid'
                ? 'That link is no longer valid — it may have been used already, or it may have expired. Ask for a new one.'
                : cause instanceof ApiError ? cause.message : 'Could not change your password.'))
            .finally(() => setBusy(false));
        }}
      >
        {error && (
          <p className="mt-4 rounded border border-(--color-bad) px-3 py-2 text-xs text-(--color-bad)">{error}</p>
        )}

        <label className="mt-5 block text-xs font-medium tracking-wide text-(--color-muted) uppercase">
          New password
          <PasswordInput name="password" required minLength={12} autoComplete="new-password" autoFocus />
        </label>

        <label className="mt-4 block text-xs font-medium tracking-wide text-(--color-muted) uppercase">
          Again
          <PasswordInput name="confirm" required minLength={12} autoComplete="new-password" />
        </label>

        <button
          type="submit" disabled={isBusy}
          className="mt-6 w-full rounded bg-(--color-brand-strong) px-3 py-2 text-sm font-medium text-(--color-brand-ink) disabled:opacity-50"
        >
          {isBusy ? 'Saving…' : 'Set the password'}
        </button>

        <button type="button" onClick={onRestart}
                className="mt-3 w-full text-center text-xs text-(--color-muted) hover:text-(--color-ink)">
          Ask for a new link
        </button>
      </form>
    </Card>
  );
}

function Card({ title, subtitle, children }: {
  title: string; subtitle: string; children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm rounded border border-(--color-line) bg-(--color-surface) p-8">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-(--color-muted)">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
            className="mt-4 w-full text-center text-xs text-(--color-muted) hover:text-(--color-ink)">
      Back to sign in
    </button>
  );
}
